import React, { useState } from 'react';
import { Box, Button, CircularProgress, Typography } from '@mui/material';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BlockIcon from '@mui/icons-material/Block';
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty';
import axios from 'axios';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { useAppDispatch, useAppSelector } from 'app/config/store';
import { fetchConsentStatus } from 'app/shared/reducers/authentication';

const ConsentBanner = () => {
  const dispatch = useAppDispatch();
  const maskedParentEmail = useAppSelector(state => state.authentication.maskedParentEmail);
  const consentStatus = useAppSelector(state => state.authentication.consentStatus);
  const canResend = useAppSelector(state => state.authentication.canResend);
  const nextResendAt = useAppSelector(state => state.authentication.nextResendAt);

  const [resending, setResending] = useState(false);

  const isDeclined = consentStatus === 'DECLINED';
  const isExpired = consentStatus === 'EXPIRED';
  const nextResendLabel = nextResendAt ? dayjs(nextResendAt).format('D MMM YYYY, h:mm A') : null;

  const bannerBg = '#E23A44';
  const textColor = '#FFFFFF';

  const handleResend = async () => {
    setResending(true);
    try {
      await axios.post('api/parental-consent/resend');
      toast.success('Consent email sent successfully.');
      dispatch(fetchConsentStatus() as any);
    } catch (err: any) {
      const msg = err?.response?.data?.message;
      toast.error(msg || 'Please wait 24 hours before resending.');
    } finally {
      setResending(false);
    }
  };

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        backgroundColor: bannerBg,
        px: { xs: 2, md: 4 },
        py: 1.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 200 }}>
          {isExpired ? (
            <HourglassEmptyIcon sx={{ fontSize: 18, color: textColor, flexShrink: 0 }} />
          ) : isDeclined ? (
            <BlockIcon sx={{ fontSize: 18, color: textColor, flexShrink: 0 }} />
          ) : (
            <AccessTimeIcon sx={{ fontSize: 18, color: textColor, flexShrink: 0 }} />
          )}
          <Typography variant="body2" sx={{ color: textColor }}>
            {isExpired ? (
              <>
                <strong>Consent request expired.</strong>{' '}
                Your 7-day consent window has passed. Please send a new request to your parent or guardian.
              </>
            ) : isDeclined ? (
              <>
                <strong>Parental consent was declined.</strong>{' '}
                <strong>{maskedParentEmail}</strong> has declined the request. Please ask them to reconsider or contact{' '}
                <a href="mailto:support@reachamy.com" style={{ color: textColor }}>support@reachamy.com</a>.
              </>
            ) : (
              <>
                <strong>Waiting for parental approval.</strong> A consent request has been sent to{' '}
                <strong>{maskedParentEmail}</strong>. Please ask your parent or guardian to check their inbox.
              </>
            )}
          </Typography>
        </Box>

        {canResend ? (
          <Button
            size="small"
            variant="outlined"
            onClick={handleResend}
            disabled={resending}
            sx={{ textTransform: 'none', fontSize: '12px', borderColor: 'rgba(255,255,255,0.5)', color: textColor, flexShrink: 0 }}
          >
            {resending ? <CircularProgress size={14} /> : isExpired ? 'Send New Request' : isDeclined ? 'Request Again' : 'Resend Email'}
          </Button>
        ) : (
          nextResendLabel && (
            <Typography variant="caption" sx={{ color: textColor, flexShrink: 0 }}>
              {isExpired ? 'Can send new request after' : isDeclined ? 'Can request again after' : 'Can resend after'} <strong>{nextResendLabel}</strong>
            </Typography>
          )
        )}
      </Box>

    </Box>
  );
};

export default ConsentBanner;